import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser, LoginResponse, Role } from './types';
import { restoreAccessTokenFromRefresh } from '@/core/api-client/client';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getStoredUserJson,
  setAccessToken,
  setRefreshToken,
  setStoredUserJson,
} from './tokenStorage';
import { login as loginApi } from '@/features/auth/api';
import { roleHomePath } from '@/core/permissions';

interface AuthContextValue {
  user: AuthUser | null;
  role: Role | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<LoginResponse>;
  loginAsDevUser: (user: AuthUser) => void;
  setUser: (user: AuthUser) => void;
  logout: () => void;
  homePath: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredUser(): AuthUser | null {
  const raw = getStoredUserJson();
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapSession() {
      const stored = readStoredUser();
      const refreshToken = getRefreshToken();

      // #region agent log
      fetch('http://127.0.0.1:7583/ingest/80fba74f-e190-4ce1-b0bf-ea1933550215', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '2c5c32' },
        body: JSON.stringify({
          sessionId: '2c5c32',
          runId: 'pre-fix',
          hypothesisId: 'H1',
          location: 'AuthContext.tsx:bootstrapSession',
          message: 'auth bootstrap start',
          data: { hasStoredUser: !!stored, hasRefreshToken: !!refreshToken },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion

      if (refreshToken) {
        const accessToken = await restoreAccessTokenFromRefresh();
        if (cancelled) return;

        // #region agent log
        fetch('http://127.0.0.1:7583/ingest/80fba74f-e190-4ce1-b0bf-ea1933550215', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '2c5c32' },
          body: JSON.stringify({
            sessionId: '2c5c32',
            runId: 'pre-fix',
            hypothesisId: 'H1',
            location: 'AuthContext.tsx:bootstrapSession',
            message: 'auth bootstrap refresh result',
            data: { refreshSucceeded: !!accessToken, hasStoredUser: !!stored },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion

        if (accessToken && stored) {
          setUser(stored);
        } else {
          setUser(null);
        }
      } else if (stored) {
        if (import.meta.env.DEV) {
          setUser(stored);
          setAccessToken(`dev.restored.${stored.id}.${Date.now()}`);
        } else {
          clearTokens();
          setUser(null);
        }
      }

      if (!cancelled) {
        setIsBootstrapping(false);
      }
    }

    void bootstrapSession();

    const onExpired = () => {
      clearTokens();
      setUser(null);
    };
    window.addEventListener('tln:auth-expired', onExpired);
    return () => {
      cancelled = true;
      window.removeEventListener('tln:auth-expired', onExpired);
    };
  }, []);

  const login = useCallback(async (email: string, password: string, mfaCode?: string) => {
    const result = await loginApi(email, password, mfaCode);
    setAccessToken(result.accessToken);
    setRefreshToken(result.refreshToken);
    setStoredUserJson(JSON.stringify(result.user));
    setUser(result.user);
    return result;
  }, []);

  const loginAsDevUser = useCallback((devUser: AuthUser) => {
    const fakeToken = `dev.${btoa(devUser.email)}.${Date.now()}`;
    setAccessToken(fakeToken);
    setRefreshToken(`refresh.${fakeToken}`);
    setStoredUserJson(JSON.stringify(devUser));
    setUser(devUser);
  }, []);

  const persistUser = useCallback((next: AuthUser) => {
    setStoredUserJson(JSON.stringify(next));
    setUser(next);
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role: user?.role ?? null,
      isAuthenticated: Boolean(user && getAccessToken()),
      isBootstrapping,
      login,
      loginAsDevUser,
      setUser: persistUser,
      logout,
      homePath: roleHomePath(user?.role),
    }),
    [user, isBootstrapping, login, loginAsDevUser, persistUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
